import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, Play, Pause, Mail, Clock, GitBranch, Save, X } from 'lucide-react';

interface Workflow {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  steps: WorkflowStep[];
  created_by?: { id: string; name: string };
}

interface WorkflowStep {
  id: string;
  type: 'send_email' | 'wait' | 'condition';
  order_index: number;
  config: any;
  branches?: WorkflowBranch[];
}

interface WorkflowBranch {
  condition_type: 'opened' | 'clicked' | 'not_opened' | 'not_clicked' | 'purchased' | 'not_purchased';
  steps: WorkflowStep[];
}

interface Template {
  id: string;
  name: string;
}

export default function EmailWorkflows() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    steps: [] as WorkflowStep[]
  });

  useEffect(() => {
    loadWorkflows();
    loadTemplates();
  }, []);

  const loadWorkflows = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const res = await fetch(`${apiUrl}/api/email/workflows`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setWorkflows(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const res = await fetch(`${apiUrl}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar que workflow começa com send_email
    if (formData.steps.length === 0 || formData.steps[0].type !== 'send_email') {
      alert('⚠️ O workflow deve começar com o envio de um email');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const url = editingId ? `${apiUrl}/api/email/workflows/${editingId}` : `${apiUrl}/api/email/workflows`;
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          trigger_type: 'sequence_start', // Novo tipo de trigger
          trigger_config: { initial_template_id: formData.steps[0]?.config?.template_id }
        })
      });

      const data = await res.json();
      
      if (data.success) {
        alert(editingId ? 'Workflow atualizado!' : 'Workflow criado!');
        setShowModal(false);
        setEditingId(null);
        loadWorkflows();
        resetForm();
      } else {
        alert(`Erro: ${data.error}`);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar workflow');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este workflow?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const res = await fetch(`${apiUrl}/api/email/workflows/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (data.success) {
        alert('Workflow deletado!');
        loadWorkflows();
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
      alert('Erro ao deletar workflow');
    }
  };

  const toggleActive = async (workflow: Workflow) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const res = await fetch(`${apiUrl}/api/email/workflows/${workflow.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...workflow,
          is_active: !workflow.is_active
        })
      });

      if (res.ok) {
        loadWorkflows();
      }
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    }
  };

  const handleEdit = (workflow: Workflow) => {
    setEditingId(workflow.id);
    setFormData({
      name: workflow.name,
      description: workflow.description || '',
      steps: workflow.steps || []
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      steps: []
    });
  };

  const addStep = (type: 'send_email' | 'wait' | 'condition') => {
    const newStep: WorkflowStep = {
      id: `step_${Date.now()}`,
      type,
      order_index: formData.steps.length,
      config: type === 'send_email' ? { template_id: '' } : 
              type === 'wait' ? { hours: 24 } : 
              {},
      branches: type === 'condition' ? [] : undefined
    };

    setFormData({
      ...formData,
      steps: [...formData.steps, newStep]
    });
  };

  const removeStep = (index: number) => {
    setFormData({
      ...formData,
      steps: formData.steps.filter((_, i) => i !== index)
    });
  };

  const updateStep = (index: number, field: string, value: any) => {
    const newSteps = [...formData.steps];
    newSteps[index] = {
      ...newSteps[index],
      config: {
        ...newSteps[index].config,
        [field]: value
      }
    };
    setFormData({ ...formData, steps: newSteps });
  };

  const addBranch = (stepIndex: number, conditionType: WorkflowBranch['condition_type']) => {
    const newSteps = [...formData.steps];
    if (!newSteps[stepIndex].branches) {
      newSteps[stepIndex].branches = [];
    }
    newSteps[stepIndex].branches!.push({
      condition_type: conditionType,
      steps: []
    });
    setFormData({ ...formData, steps: newSteps });
  };

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'send_email': return <Mail className="h-5 w-5" />;
      case 'wait': return <Clock className="h-5 w-5" />;
      case 'condition': return <GitBranch className="h-5 w-5" />;
      default: return null;
    }
  };

  const getConditionLabel = (type: string) => {
    const labels: Record<string, string> = {
      'opened': '✅ Abriu o email',
      'clicked': '🖱️ Clicou no link',
      'not_opened': '❌ NÃO abriu',
      'not_clicked': '❌ Clicou mas NÃO comprou',
      'purchased': '💰 Comprou',
      'not_purchased': '❌ NÃO comprou'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Sequências de Email (Workflows)</h1>
          <p className="text-gray-600 mt-1">
            Crie sequências de follow-up automáticas baseadas nas ações dos leads
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus size={20} />
          Nova Sequência
        </button>
      </div>

      {/* Info sobre como funciona */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Como funciona:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Inicie</strong> enviando um email (template de boas-vindas, oferta, etc.)</li>
          <li>• <strong>Aguarde</strong> X horas/dias para o lead interagir</li>
          <li>• <strong>Crie condições</strong> baseadas na ação do lead (abriu, clicou, comprou)</li>
          <li>• <strong>Envie follow-ups</strong> diferentes para cada cenário</li>
          <li>• A sequência continua até converter o lead ou ele sair</li>
        </ul>
      </div>

      <div className="grid gap-4">
        {workflows.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Mail size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">Nenhuma sequência criada ainda</p>
            <p className="text-sm text-gray-500">
              Clique em "Nova Sequência" para criar sua primeira automação de follow-up
            </p>
          </div>
        ) : (
          workflows.map(workflow => (
            <div key={workflow.id} className="bg-white p-6 rounded-lg border hover:shadow-lg transition">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{workflow.name}</h3>
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                      workflow.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {workflow.is_active ? '✓ Ativo' : '⏸ Inativo'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{workflow.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>📧 {workflow.steps?.length || 0} passos</span>
                    {workflow.created_by && <span>👤 {workflow.created_by.name}</span>}
                    <span>📅 {new Date(workflow.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(workflow)}
                    className={`p-2 rounded hover:bg-gray-100 ${
                      workflow.is_active ? 'text-green-600' : 'text-gray-400'
                    }`}
                    title={workflow.is_active ? 'Desativar' : 'Ativar'}
                  >
                    {workflow.is_active ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                  <button
                    onClick={() => handleEdit(workflow)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title="Editar"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(workflow.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Deletar"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>

              {/* Preview da sequência */}
              <div className="border-t pt-4 mt-4">
                <p className="text-xs text-gray-500 mb-3 font-medium uppercase">Preview da Sequência:</p>
                <div className="flex flex-wrap gap-2">
                  {workflow.steps?.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <div className={`flex items-center gap-2 px-3 py-1 rounded text-sm ${
                        step.type === 'send_email' ? 'bg-blue-100 text-blue-700' :
                        step.type === 'wait' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {getStepIcon(step.type)}
                        <span>
                          {step.type === 'send_email' ? 'Enviar Email' :
                           step.type === 'wait' ? `Aguardar ${step.config?.hours || 24}h` :
                           'Condição'}
                        </span>
                      </div>
                      {idx < (workflow.steps?.length || 0) - 1 && (
                        <span className="text-gray-400">→</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
                <h2 className="text-2xl font-bold">
                  {editingId ? 'Editar' : 'Nova'} Sequência de Email
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Sequência *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="Ex: Sequência de Boas-vindas, Follow-up Carrinho Abandonado..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      rows={2}
                      placeholder="Descreva o objetivo desta sequência..."
                    />
                  </div>
                </div>

                {/* Passos da Sequência */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Passos da Sequência</h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => addStep('send_email')}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                        disabled={formData.steps.length === 0 && formData.steps.length > 0}
                      >
                        <Mail size={16} />
                        Enviar Email
                      </button>
                      <button
                        type="button"
                        onClick={() => addStep('wait')}
                        className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 flex items-center gap-1"
                        disabled={formData.steps.length === 0}
                      >
                        <Clock size={16} />
                        Aguardar
                      </button>
                      <button
                        type="button"
                        onClick={() => addStep('condition')}
                        className="bg-purple-500 text-white px-3 py-1 rounded text-sm hover:bg-purple-600 flex items-center gap-1"
                        disabled={formData.steps.length === 0}
                      >
                        <GitBranch size={16} />
                        Condição
                      </button>
                    </div>
                  </div>

                  {formData.steps.length === 0 && (
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Mail size={48} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 mb-2">Comece sua sequência enviando um email</p>
                      <p className="text-sm text-gray-500">
                        Clique em "Enviar Email" para adicionar o primeiro passo
                      </p>
                    </div>
                  )}

                  {/* Lista de Passos */}
                  <div className="space-y-4">
                    {formData.steps.map((step, index) => (
                      <div key={step.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2">
                            <span className="bg-white rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm border">
                              {index + 1}
                            </span>
                            <div className={`flex items-center gap-2 px-3 py-1 rounded font-medium ${
                              step.type === 'send_email' ? 'bg-blue-100 text-blue-700' :
                              step.type === 'wait' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {getStepIcon(step.type)}
                              <span>
                                {step.type === 'send_email' ? 'Enviar Email' :
                                 step.type === 'wait' ? 'Aguardar' :
                                 'Condição (If/Else)'}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeStep(index)}
                            className="text-red-600 hover:bg-red-50 p-1 rounded"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        {/* Configuração do Passo */}
                        {step.type === 'send_email' && (
                          <div className="ml-10">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Template de Email *
                            </label>
                            <select
                              value={step.config.template_id || ''}
                              onChange={(e) => updateStep(index, 'template_id', e.target.value)}
                              className="w-full border border-gray-300 rounded px-3 py-2"
                              required
                            >
                              <option value="">Selecione um template...</option>
                              {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              {index === 0 ? '📧 Este é o email inicial da sequência' : '📧 Email de follow-up'}
                            </p>
                          </div>
                        )}

                        {step.type === 'wait' && (
                          <div className="ml-10">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Tempo de Espera
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={step.config.hours || 24}
                                onChange={(e) => updateStep(index, 'hours', parseInt(e.target.value))}
                                className="w-32 border border-gray-300 rounded px-3 py-2"
                                min="1"
                              />
                              <span className="text-gray-600">horas</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              ⏰ Aguardar antes de executar o próximo passo
                            </p>
                          </div>
                        )}

                        {step.type === 'condition' && (
                          <div className="ml-10 space-y-3">
                            <p className="text-sm text-gray-600">
                              Verifique a ação do lead após o email anterior:
                            </p>
                            
                            <div className="space-y-2">
                              {['opened', 'clicked', 'not_opened', 'not_clicked'].map((cond) => (
                                <button
                                  key={cond}
                                  type="button"
                                  onClick={() => addBranch(index, cond as any)}
                                  className="w-full text-left border border-gray-300 rounded px-3 py-2 hover:bg-white text-sm"
                                >
                                  {getConditionLabel(cond)}
                                </button>
                              ))}
                            </div>

                            {step.branches && step.branches.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {step.branches.map((branch, branchIdx) => (
                                  <div key={branchIdx} className="bg-white border rounded p-2">
                                    <div className="font-medium text-sm">
                                      {getConditionLabel(branch.condition_type)}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                      → Adicione ações para este cenário
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 sticky bottom-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
                  disabled={formData.steps.length === 0}
                >
                  <Save size={18} />
                  {editingId ? 'Atualizar' : 'Criar'} Sequência
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
